import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import PatientService from '../../../api/services/PatientService';
import { ConfirmUserAttributeInput } from 'aws-amplify/auth';

const ACTIONS_PREFIX = 'patient';

type PatientState = {
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
  updatedPatient: fhir4.Patient | null;
};

const initialState: PatientState = {
  isLoading: false,
  error: null,
  successMessage: null,
  updatedPatient: null,
};

export const updatePatientThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updatePatient`,
  async (
    params: {
      userData: any;
      accessToken: string;
      uuid: string;
      callback: (success: boolean) => void;
    },
    { rejectWithValue },
  ) => {
    const { userData, uuid, callback } = params;
    try {
      const response = await PatientService.updateUser(userData, uuid, callback);
      callback(true);

      return response;
    } catch (error) {
      callback(false);
      return rejectWithValue(error);
    }
  },
);

export const sendVerificationCode = createAsyncThunk(
  `${ACTIONS_PREFIX}/sendVerificationCode`,
  async (params: any) => {
    return await PatientService.sendVerificationCode(params);
  },
);

export const verifyPhoneCode = createAsyncThunk(
  `${ACTIONS_PREFIX}/verifyPhoneCode`,
  async (params: { verCodeResUUID: string; verCode: string }) => {
    return await PatientService.verifyPhoneCode(params);
  },
);

export const verifyEmailCode = createAsyncThunk(
  `${ACTIONS_PREFIX}/verifyEmailCode`,
  async ({
    userAttributeKey,
    confirmationCode,
    params,
  }: ConfirmUserAttributeInput & { params: any }) => {
    return await PatientService.verifyEmailCode({ userAttributeKey, confirmationCode, params });
  },
);

export const editPhone = createAsyncThunk(`${ACTIONS_PREFIX}/editPhone`, async (params: any) => {
  return await PatientService.editPhone(params);
});

export const editEmail = createAsyncThunk(`${ACTIONS_PREFIX}/editEmail`, async (email: string) => {
  return await PatientService.editEmail(email);
});

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    clearPatientData: (state) => {
      state.updatedPatient = null;
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updatePatientThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updatePatientThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Patient update failed';
        state.successMessage = null;
      });

    builder
      .addCase(sendVerificationCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendVerificationCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendVerificationCode.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(editPhone.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(editPhone.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(editPhone.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(editEmail.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(editEmail.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(editEmail.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { clearPatientData } = patientSlice.actions;

export const patientState = (state: RootState) => state.patient;
export default patientSlice.reducer;
